/**
 * userTypes.js
 *
 * Single source of truth for account types and user types across both the
 * Assessment and Referral Management System (RMS) products.
 *
 * These constants replace scattered inline string literals throughout the
 * frontend codebase. Import from here rather than hardcoding strings.
 *
 * Architecture:
 *   - accountType determines which PRODUCT a user belongs to.
 *   - userType determines their ROLE within that product.
 *   - Together they form the two-axis classification used throughout Firebase
 *     Auth custom claims, Firestore security rules, and route guards.
 */

// ─── Account Types ───────────────────────────────────────────────────────────
// Each product has its own distinct accountType values in Firebase Auth claims.

export const ACCOUNT_TYPES = {
  ASSESSMENT_PROVIDER: 'Provider',
  ASSESSMENT_SERVICE_USER: 'Service User',
  RMS_PROVIDER: 'RMS Provider User',
  RMS_SERVICE_USER: 'RMS Service User',
};

// ─── User Types (Roles) ─────────────────────────────────────────────────────
// Role values within each product, stored in the userType custom claim.

export const ASSESSMENT_PROVIDER_USER_TYPES = ['Clinician', 'Support', 'Org. Admin'];
export const ASSESSMENT_SERVICE_USER_TYPES = ['Patient', 'Informant'];

export const RMS_PROVIDER_USER_TYPES = ['RMS Clinician', 'RMS Support', 'RMS Org. Admin'];
export const RMS_SERVICE_USER_TYPES = ['RMS School Representative', 'RMS Legal Guardian'];
// 'RMS Teacher' (TDI-489) is deliberately kept out of RMS_SERVICE_USER_TYPES:
// a class teacher gets the teacher task list and their allocated school forms,
// never the SENCO or family surfaces those route lists admit. Mirrors
// python-functions/constants/user_types.py.
export const RMS_TEACHER_USER_TYPE = 'RMS Teacher';

// Named RMS provider roles — prefer these over inline string literals.
export const RMS_PROVIDER_ROLES = {
  ORG_ADMIN: 'RMS Org. Admin',
  SUPPORT: 'RMS Support',
  CLINICIAN: 'RMS Clinician',
};

// Roles permitted to reach RMS staff management. RMS Org. Admin has full control;
// RMS Support is read-only (see RmsStaffDashboard). RMS Clinician has no access.
export const RMS_STAFF_MANAGEMENT_ROLES = [RMS_PROVIDER_ROLES.ORG_ADMIN, RMS_PROVIDER_ROLES.SUPPORT];

// Display labels for RMS provider user types — the staff-management UI shows
// these (without the internal "RMS " prefix). The stored userType values and all
// role gating continue to use the prefixed RMS_PROVIDER_USER_TYPES values.
export const RMS_PROVIDER_USER_TYPE_LABELS = {
  [RMS_PROVIDER_ROLES.ORG_ADMIN]: 'Org. Admin',
  [RMS_PROVIDER_ROLES.SUPPORT]: 'Support',
  [RMS_PROVIDER_ROLES.CLINICIAN]: 'Clinician',
};

/**
 * Display label for an RMS provider userType with the internal "RMS " prefix
 * stripped (e.g. 'RMS Clinician' -> 'Clinician'). Falls back to the raw value,
 * or '-' when empty. Display only — never use for role checks or API payloads.
 */
export const getRmsUserTypeLabel = (userType) =>
  RMS_PROVIDER_USER_TYPE_LABELS[userType] || userType || '-';

// ─── Clinician specialism (triage team) ──────────────────────────────────────
// The ASD / ADHD / both label carried by an RMS Clinician. Drives which triage
// queues/patients they can access (A4, sequenced later).
export const CLINICIAN_SPECIALISM = { ASD: 'asd', ADHD: 'adhd', BOTH: 'both' };
export const CLINICIAN_SPECIALISMS = [
  CLINICIAN_SPECIALISM.ASD,
  CLINICIAN_SPECIALISM.ADHD,
  CLINICIAN_SPECIALISM.BOTH,
];
export const CLINICIAN_SPECIALISM_LABELS = {
  [CLINICIAN_SPECIALISM.ASD]: 'ASD',
  [CLINICIAN_SPECIALISM.ADHD]: 'ADHD',
  [CLINICIAN_SPECIALISM.BOTH]: 'ASD & ADHD',
};

// ─── Canonical Discriminator Functions ───────────────────────────────────────
// accountType alone determines which product a user belongs to.

/**
 * Returns true if claims indicate an RMS user (provider or service user).
 * Uses accountType as the sole discriminator.
 */
export const isRmsUser = (claims) => {
  if (!claims) return false;
  return (
    claims.accountType === ACCOUNT_TYPES.RMS_PROVIDER ||
    claims.accountType === ACCOUNT_TYPES.RMS_SERVICE_USER
  );
};

/**
 * Returns true if claims indicate an RMS provider user.
 */
export const isRmsProvider = (claims) => {
  return claims?.accountType === ACCOUNT_TYPES.RMS_PROVIDER;
};

/**
 * Returns true if the user may triage (start/complete a triage review): an RMS
 * Clinician, or a Clinical Org. Admin (an RMS Org. Admin flagged
 * isAssignableClinician). RMS Support and Non-Clinical Org. Admins cannot.
 */
export const canTriage = (claims) => {
  if (!isRmsProvider(claims)) return false;
  return (
    claims.userType === RMS_PROVIDER_ROLES.CLINICIAN ||
    (claims.userType === RMS_PROVIDER_ROLES.ORG_ADMIN && claims.isAssignableClinician === true)
  );
};

/**
 * Returns true if the user may OPEN the triage proforma and letter-review
 * screens. This matches the ProtectedRoute allowedRoles for those routes
 * (['RMS Clinician', 'RMS Org. Admin']) — non-clinical org admins reach them
 * read-only, but RMS Support is bounced by the route guard. Use this to gate
 * the buttons that navigate there, so RMS Support never sees a control that
 * would just kick them back out.
 */
export const canOpenTriageScreens = (claims) => {
  if (!isRmsProvider(claims)) return false;
  return (
    claims.userType === RMS_PROVIDER_ROLES.CLINICIAN ||
    claims.userType === RMS_PROVIDER_ROLES.ORG_ADMIN
  );
};

/**
 * Returns true if the user may SEND an already signed-off triage letter to its
 * recipients (TD-228). Once a clinician has signed the letter off (TD-249),
 * sending is a clerical action performed manually from the documents page.
 * Who may perform it depends on the letter's pathway:
 *   - ADHD: any RMS provider user - clinician, org admin or support.
 *   - Autism ('asd'): org admins and support only; clinicians hand over to
 *     the support/admin team after sign-off.
 * Service users never can. Note this is broader than canTriage (who may sign
 * off): support users can send but not sign off.
 */
export const canSendTriageLetter = (claims) => {
  // Tom King, 23 and 24 October 2026 (TDI-460, TDI-387): only the admin roles
  // send, on both pathways. Clinicians and Clinical Org Admins (an RMS Org.
  // Admin flagged isAssignableClinician, who act as clinicians) sign off and
  // hand over; sending is what starts admin's EMIS work. This replaces the
  // TD-228 rule (any provider staff) and the autism-only RMS-195 narrowing.
  if (!isRmsProvider(claims)) return false;
  if (claims.userType === RMS_PROVIDER_ROLES.SUPPORT) return true;
  return (
    claims.userType === RMS_PROVIDER_ROLES.ORG_ADMIN &&
    claims.isAssignableClinician !== true
  );
};

/**
 * Returns true if the user may mark a referral Ready for triage
 * (LSC-038/LSC-044). The ready decision is an administrative gate, not a
 * clinical judgement, and was agreed to sit with "any user" — so every RMS
 * provider role (clinician, org admin, support) may make it. Enforced
 * server-side by rmsMarkReadyForTriage.
 */
export const canMarkReadyForTriage = (claims) => isRmsProvider(claims);

/**
 * Returns true if claims indicate an RMS service user.
 */
export const isRmsServiceUser = (claims) => {
  return claims?.accountType === ACCOUNT_TYPES.RMS_SERVICE_USER;
};

/**
 * Returns true if claims indicate an Assessment provider.
 */
export const isAssessmentProvider = (claims) => {
  return claims?.accountType === ACCOUNT_TYPES.ASSESSMENT_PROVIDER;
};

/**
 * Returns true if claims indicate an Assessment service user.
 */
export const isAssessmentServiceUser = (claims) => {
  return claims?.accountType === ACCOUNT_TYPES.ASSESSMENT_SERVICE_USER;
};
